import { Command } from 'commander';
import { ServiceTokenManager } from '@whittlelabs/keep';
import type { BrandConfig } from '../branding/types';
import { resolvePaths, resolveProfile } from '../branding/apply';
import { PairingConfigStore } from '../pairing/store';
import { listPairings } from '../pairing/pairings';

/**
 * `<brand> list` - show all paired machines for the calling user, scoped
 * to this whitelabel's OAuth client.
 *
 * Uses the locally-persisted pairing credentials to mint a Keep service
 * token, then calls Keep's service-identities listing endpoint.
 */
export function buildListCommand(brand: BrandConfig): Command {
  return new Command('list')
    .description(`List paired ${brand.product.title} machines`)
    .option('-p, --profile <name>', 'Config profile whose pairing credentials to use (default "default")')
    .action(async (options: { profile?: string }) => {
      try {
        const paths = resolvePaths(brand, resolveProfile(brand, options.profile));
        const store = new PairingConfigStore(paths.configFile);
        const pairing = store.read();

        const tokens = new ServiceTokenManager({
          keepApiUrl: pairing.keepApiUrl,
          clientId: pairing.credentials.clientId,
          clientSecret: pairing.credentials.clientSecret,
        });
        const accessToken = await tokens.getToken();
        if (!accessToken) {
          throw new Error('Failed to mint service token from stored credentials.');
        }

        const machines = await listPairings({ brand, accessToken });
        if (machines.length === 0) {
          console.log('No paired machines.');
          return;
        }
        console.log(['id', 'name', 'hostname', 'pairedAt', 'lastSeenAt', 'active'].join('\t'));
        for (const m of machines) {
          console.log(
            [m.id, m.name, m.hostname ?? '-', m.pairedAt ?? '-', m.lastSeenAt ?? '-', m.active].join('\t'),
          );
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        console.error(`list failed: ${message}`);
        process.exit(1);
      }
    });
}
